import {
  addressFormSchema,
  latLonFormSchema,
  optionalLatLonFormSchema,
} from 'components/forms/address-form/address-form.schema';
import { array, boolean, number, object, string, mixed } from 'yup';
import { addChargesFormSchema } from '../add-charges-form/add-charges-form.schema';
import { addTaxFormSchema } from '../add-tax-form/add-tax-form.schema';
import {
  getInitialLanguageCode,
  makeDisplayInformationSchema,
} from '../fields/display-information-field/display-information-field.utils';
import { posLocationGuidMapping } from 'api/ordering-service/ordering-service.utils';
import i18n from 'app-i18n';
import {
  keySchema,
  valueSchema,
} from '../fields/key-value-field/key-value-field.component';
import { orderOptionSchema } from '../order-option-form/order-option-form.hooks';

export const locationDisplayInformationSchema = object({
  displayInfo: makeDisplayInformationSchema(
    object({
      title: string().required().max(100),
      shortDescription: string().required().max(500),
      longDescription: string().required().max(1000),
      smallImage: string().singleImage().nullable(),
      mediumImage: string().singleImage().nullable(),
    }),
    [
      {
        languageCode: getInitialLanguageCode(
          navigator.language.split('-')[0].toUpperCase(),
        ),
        title: '',
        shortDescription: '',
        longDescription: '',
        smallImage: '',
        mediumImage: '',
      },
    ],
  ),
});

export const storeSchema = object({
  title: string().required().max(50).default(''),
  isCatering: boolean().default(false),
  isMenuBrowsing: boolean().default(false),
  isTest: boolean().default(false),
  // Remove the requirement and phone number format validation, because some locations do not have a phone number or have invalid phone numbers
  phoneNumber: string().nullable(),
  viewOnlyLocationEnabled: boolean().nullable().flexDefault(false),
})
  .concat(addressFormSchema.concat(optionalLatLonFormSchema))
  .concat(locationDisplayInformationSchema);

const paymentSettingsSchema = object({
  currencyCode: string().nullable().default('USD'),
  paymentGatewayConfig: object({
    additionalExtendedAttributes: array()
      .of(object({ key: keySchema, value: valueSchema }))
      .default([]),
  }),
  disableTipping: boolean().required().default(false),
  taxes: array().of(addTaxFormSchema).default([]),
  charges: array().of(addChargesFormSchema).default([]),
});

const orderingConfigurationSchema = object({
  orderProcessingConfig: object({
    paymentGatewayName: string().nullable().default(''),
    paymentIDGenerationStrategy: string().nullable().default(''),
    orderProcessingStrategy: string().nullable().default(''),
    roundingMode: string().nullable().default(''),
    calculationStrategy: string().nullable().default(''),
    consolidateMultipleDiscounts: boolean().required().default(false),
    realTimeLoyaltyProcessing: boolean().required().default(false),
    externalDeliverySettingsEnabled: boolean().required().default(false),
    externalDeliverySettings: object()
      .when('externalDeliverySettingsEnabled', {
        is: true,
        then: schema =>
          schema.shape({
            deliveryStrategy: string().required().flexDefault(''),
            externalDeliveryFeeStrategy: boolean()
              .required()
              .flexDefault(false),
            externalDeliveryChargeId: string().flexDefault('').max(100),
            pickupBusinessName: string().required().flexDefault(''),
            pickupInstructions: string().max(280).flexDefault(''),
            contactPhone: string().required().flexDefault(''),
            pickupAddress: object({
              street1: string().required().max(100).flexDefault(''),
              street2: string().max(100).nullable().flexDefault(''),
              city: string().required().max(100).flexDefault(''),
              state: string().required().flexDefault(''),
              postalCode: string().required().max(50).flexDefault(''),
              country: string().required().flexDefault(''),
            }).flexDefault({}),
          }),
        otherwise: schema =>
          schema.shape({
            deliveryStrategy: string().flexDefault(''),
            externalDeliveryFeeStrategy: boolean().flexDefault(false),
            externalDeliveryChargeId: string().flexDefault('').max(100),
            pickupBusinessName: string().flexDefault(''),
            pickupInstructions: string().max(280).flexDefault(''),
            contactPhone: string().flexDefault(''),
            pickupAddress: object({
              street1: string().max(100).flexDefault(''),
              street2: string().max(100).nullable().flexDefault(''),
              city: string().max(100).flexDefault(''),
              state: string().flexDefault(''),
              postalCode: string().max(50).flexDefault(''),
              country: string().flexDefault(''),
            }).flexDefault({}),
          }),
      })
      .flexDefault({}),
  })
    .nullable()
    .flexDefault({}),
});

const pickupSettingsSchema = object({
  pickupOrdersAccepted: boolean().required().default(false),
  pickupClosedToday: boolean().default(false),
  pickupSevenPlusDaysOrdering: boolean().required().default(false),
  enableFulFillTextForToast: boolean().nullable().required().flexDefault(false),
  fulFillTextForToast: string()
    .nullable()
    .default('')
    .when('enableFulFillTextForToast', {
      is: true,
      then: schema => schema.required().max(160),
    }),
  pickupMaxDaysOut: number()
    .nullable()
    .when(['pickupOrdersAccepted', 'pickupSevenPlusDaysOrdering'], {
      is: (pickupOrdersAccepted, pickupSevenPlusDaysOrdering) =>
        pickupOrdersAccepted && pickupSevenPlusDaysOrdering,
      then: schema =>
        schema
          .required()
          .min(8, () =>
            i18n.t('pickupSettings.pickupGeneral.noOfDaysInAdvance.errors.min'),
          )
          .max(365, () =>
            i18n.t('pickupSettings.pickupGeneral.noOfDaysInAdvance.errors.max'),
          ),
    }),
  pickupAsapSupported: boolean().required().default(false),
  showPickupTimeSlots: boolean().required().default(false),
  pickupOrderProcessingTime: number()
    .nullable()
    .when('pickupOrdersAccepted', {
      is: pickupOrdersAccepted => pickupOrdersAccepted,
      then: schema =>
        schema
          .required()
          .min(1)
          .max(1439, () =>
            i18n.t(
              'pickupSettings.pickupAvailability.pickupOrderPrepTime.errors.max',
            ),
          ),
    }),
  pickupAvailabilityInterval: number()
    .nullable()
    .when('pickupOrdersAccepted', {
      is: pickupOrdersAccepted => pickupOrdersAccepted,
      then: schema =>
        schema
          .required()
          .min(0)
          .max(1439, () =>
            i18n.t(
              'pickupSettings.pickupAvailability.timeSlotInterval.errors.max',
            ),
          ),
    }),
  pickupOrderNoticePeriod: number()
    .nullable()
    .when('pickupOrdersAccepted', {
      is: pickupOrdersAccepted => pickupOrdersAccepted,
      then: schema =>
        schema
          .min(0)
          .max(525599, () =>
            i18n.t(
              'pickupSettings.pickupAvailability.pickupOrderNoticePeriod.errors.max',
            ),
          ),
    }),
  pickupPaymentConfigs: object({
    orderType: string().default('PICKUP'),
    guestCheckoutSupported: boolean().default(false),
    placePOSOrdersAsOpen: boolean().default(false),
    paymentModes: string().nullable().default('PAYMENT_MANDATORY'),
  }),
  pickupOrderValueLimits: mixed().when('pickupOrdersAccepted', {
    is: true,
    then: object({
      orderValueLimitsEnabled: boolean().required().default(false),
      minOrderValue: number()
        .nullable()
        .when('orderValueLimitsEnabled', {
          is: true,
          then: schema =>
            schema.min(0).when('maxOrderValue', {
              is: value => value == null,
              then: schema => schema.required(),
            }),
        }),

      maxOrderValue: number()
        .nullable()
        .when('orderValueLimitsEnabled', {
          is: true,
          then: schema =>
            schema.min(0).test(
              'maxGreaterThanMin',
              () =>
                i18n.t(
                  'pickupSettings.minMax.fields.maxOrderValue.errors.maxGreaterThanMin',
                ),
              (value, context) => {
                return value == null || value > context.parent.minOrderValue;
              },
            ),
        }),
      displayMessage: mixed().when('orderValueLimitsEnabled', {
        is: true,
        then: () =>
          makeDisplayInformationSchema(
            object({
              shortMessage: string().required(),
              longMessage: string().required(),
            }),
            [
              {
                languageCode: getInitialLanguageCode(
                  navigator.language.split('-')[0].toUpperCase(),
                ),
                shortMessage: '',
                longMessage: '',
              },
            ],
          ),
        otherwise: () => array().default([]),
      }),
    }),
    otherwise: () => mixed().default({}),
  }),
  pickupOrderOptions: array().of(orderOptionSchema).default([]),
});

const deliverySettingsSchema = object({
  deliveryOrdersAccepted: boolean().required().default(false),
  deliveryClosedToday: boolean().default(false),
  deliverySevenPlusDaysOrdering: boolean().required().default(false),
  deliveryMaxDaysOut: number()
    .nullable()
    .when(['deliveryOrdersAccepted', 'deliverySevenPlusDaysOrdering'], {
      is: (deliveryOrdersAccepted, deliverySevenPlusDaysOrdering) =>
        deliveryOrdersAccepted && deliverySevenPlusDaysOrdering,
      then: schema =>
        schema
          .required()
          .min(8, () =>
            i18n.t('pickupSettings.pickupGeneral.noOfDaysInAdvance.errors.min'),
          )
          .max(365, () =>
            i18n.t('pickupSettings.pickupGeneral.noOfDaysInAdvance.errors.max'),
          ),
    }),
  deliveryAsapSupported: boolean().required().default(false),
  showDeliveryTimeSlots: boolean().required().default(false),
  deliveryOrderProcessingTime: number()
    .nullable()
    .when('deliveryOrdersAccepted', {
      is: deliveryOrdersAccepted => deliveryOrdersAccepted,
      then: schema =>
        schema
          .required()
          .min(1)
          .max(1439, () =>
            i18n.t(
              'deliverySettings.deliveryAvailability.deliveryOrderPrepTime.errors.max',
            ),
          ),
    }),
  deliveryAvailabilityInterval: number()
    .nullable()
    .when('deliveryOrdersAccepted', {
      is: deliveryOrdersAccepted => deliveryOrdersAccepted,
      then: schema =>
        schema
          .required()
          .min(0)
          .max(1439, () =>
            i18n.t(
              'deliverySettings.deliveryAvailability.timeSlotInterval.errors.max',
            ),
          ),
    }),
  deliveryOrderNoticePeriod: number()
    .nullable()
    .when('deliveryOrdersAccepted', {
      is: deliveryOrdersAccepted => deliveryOrdersAccepted,
      then: schema =>
        schema
          .min(0)
          .max(525599, () =>
            i18n.t(
              'deliverySettings.deliveryAvailability.noticePeriod.errors.max',
            ),
          ),
    }),
  deliveryPaymentConfigs: object({
    orderType: string().default('DELIVERY'),
    guestCheckoutSupported: boolean().default(false),
    placePOSOrdersAsOpen: boolean().default(false),
    paymentModes: string().nullable().default('PAYMENT_MANDATORY'),
  }),
  deliveryOrderValueLimits: mixed().when('deliveryOrdersAccepted', {
    is: true,
    then: object({
      orderValueLimitsEnabled: boolean().required().default(false),
      minOrderValue: number()
        .nullable()
        .when('orderValueLimitsEnabled', {
          is: true,
          then: schema =>
            schema.min(0).when('maxOrderValue', {
              is: value => value == null,
              then: schema => schema.required(),
            }),
        }),

      maxOrderValue: number()
        .nullable()
        .when('orderValueLimitsEnabled', {
          is: true,
          then: schema =>
            schema.min(0).test(
              'maxGreaterThanMin',
              () =>
                i18n.t(
                  'pickupSettings.minMax.fields.maxOrderValue.errors.maxGreaterThanMin',
                ),
              (value, context) => {
                return value == null || value > context.parent.minOrderValue;
              },
            ),
        }),
      displayMessage: mixed().when('orderValueLimitsEnabled', {
        is: true,
        then: () =>
          makeDisplayInformationSchema(
            object({
              shortMessage: string().required(),
              longMessage: string().required(),
            }),
            [
              {
                languageCode: getInitialLanguageCode(
                  navigator.language.split('-')[0].toUpperCase(),
                ),
                shortMessage: '',
                longMessage: '',
              },
            ],
          ),
        otherwise: () => array().default([]),
      }),
    }),
    otherwise: () => mixed().default({}),
  }),
  deliveryOrderOptions: array().of(orderOptionSchema).default([]),
  deliveryRadius: number()
    .nullable()
    .when(['deliveryOrdersAccepted', 'deliveryArea.deliveryAreaType'], {
      is: (deliveryOrdersAccepted, deliveryAreaType) =>
        deliveryOrdersAccepted && deliveryAreaType === 'CIRCLE',
      then: schema => schema.required().min(0),
    }),
  deliveryArea: mixed()
    .when('deliveryOrdersAccepted', {
      is: true,
      then: object({
        deliveryAreaType: string().required().default('CIRCLE'),
        deliveryAreaPolygon: array()
          .of(latLonFormSchema)
          .flexDefault([])
          .when('deliveryAreaType', {
            is: 'POLYGON',
            then: schema => schema.min(3),
          }),
      }),
      otherwise: () => mixed().nullable(),
    })
    .flexDefault({}),
  latitude: number()
    .nullable()
    .when('deliveryOrdersAccepted', {
      is: true,
      then: schema => schema.min(-89.99999999).max(90).required(),
    }),
  longitude: number()
    .nullable()
    .when('deliveryOrdersAccepted', {
      is: true,
      then: schema => schema.min(-179.99999999).max(180).required(),
    }),
});

const orderingSchema = object({
  pos: object({
    type: string().default('incentivio-order'),
    storeId: string()
      .default('')
      .when('type', (posType, schema) => {
        return !!posLocationGuidMapping[posType]?.menu
          ? schema.required()
          : schema;
      }),
  }),
  deliveryOffDays: array().required().default([]),
  deliveryOrderHours: array().required().default([]),
  deliveryOrderOptions: array().required().default([]),
  deliveryTemporaryHours: array().nullable().default([]),
  extendedAttributes: object().default({}),
  additionalExtendedAttributes: array()
    .required()
    .of(object({ key: keySchema, value: valueSchema }))
    .default([]),
  failureNotificationRecipientEmailList: array().required().default([]),
  kitchenThrottleConfig: object({
    countDefault: number().nullable(),
    valueDefault: number().nullable(),
    displayInfo: object().default({}),
    timeslots: object().default({}),
  }).nullable(),
  menuImportConfig: object({
    connectorGatewayName: string().nullable().flexDefault(''),
    connectorName: string().nullable(),
    defaultLanguage: string().nullable(),
    extStoreId: string().nullable(),
    extendedAttributes: object().default({}),
    additionalExtendedAttributes: array()
      .of(object({ key: keySchema, value: valueSchema }))
      .default([]),
    posEntryProperties: array().default([]),
    posSwitchProperties: array().default([]),
    importType: string().nullable(),
    overrideExistingImages: boolean(),
  }).default({ connectorGatewayName: '', extendedAttributes: {} }),
  orderNotificationRecipientEmailList: array().required().default([]),

  orderThrottleConfig: array().nullable().required().default([]),
  paymentConfigs: array().required().default([]),
  pickupOffDays: array().required().default([]),
  pickupOrderHours: array().required().default([]),
  pickupTemporaryHours: array().nullable().default([]),
  timeZone: string().required().default(''),
  extPosLocationId: string().default('').nullable(),
  primaryLocation: boolean().default(true),
  primaryLocationId: string().default('').nullable(),
  menuVisibility: mixed().default({}),
  qalocation: boolean().required().default(false),
  orderNotificationRecipientEmail: object({
    enabled: boolean().default(false),
    list: array().when('enabled', {
      is: true,
      then: array()
        .min(1)
        .test(
          'validate-emails',
          () =>
            i18n.t(
              'forms.locationInfoForm.locationEmailNotifications.fields.orderNotificationRecipientEmailList.errors.validateEmails',
            ),
          list => {
            if (!list || !list.length) return true;
            const emailValidator = string().email().trim();
            return list.every(email => emailValidator.isValidSync(email));
          },
        ),
    }),
  }),
  failureNotificationRecipientEmail: object({
    enabled: boolean().default(false),
    list: array().when('enabled', {
      is: true,
      then: array()
        .min(1)
        .test(
          'validate-emails',
          () =>
            i18n.t(
              'forms.locationInfoForm.locationEmailNotifications.fields.failureNotificationRecipientEmailList.errors.validateEmails',
            ),
          list => {
            if (!list || !list.length) return true;
            const emailValidator = string().email().trim();
            return list.every(email => emailValidator.isValidSync(email));
          },
        ),
    }),
  }),
  externalOrderingEnabled: boolean().default(false),
  externalOrderingText: string()
    .nullable()
    .when('externalOrderingEnabled', {
      is: true,
      then: schema => schema.url().required(),
    }),
})
  .concat(paymentSettingsSchema)
  .concat(orderingConfigurationSchema)
  .concat(pickupSettingsSchema)
  .concat(deliverySettingsSchema);

export const locationFormSchema = object({
  store: storeSchema,
  ordering: orderingSchema,
});
